import React, { useCallback, useState } from "react";
import {
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  UncontrolledDropdown,
} from "reactstrap";
import { toast } from "react-toastify";

import ExportLoadingModal from "Components/Common/ExportLoadingModal";
import Permission from "Components/Common/Permission";
import DataImportModal from "./DataImportModal";
import { DataImportConfig } from "./dataImportFields";

/**
 * Import/export menu shown next to the Add button on the data type lists.
 *
 * The export sends the table's current filters and sort, so what is downloaded
 * matches what is on screen.
 */
const ImportExportMenu = ({
  resource,
  config,
  exportFn,
  exportFileName,
  apiParams,
  onImported,
}: {
  /** Permission resource, e.g. "contract-type". */
  resource: string;
  config: DataImportConfig;
  exportFn: (params: any) => Promise<any>;
  /** Downloaded file name, e.g. "Contract_Types_Export.csv". */
  exportFileName: string;
  apiParams?: any;
  /** Refreshes the table once an import has created records. */
  onImported?: () => void;
}) => {
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);

  const [exporting, setExporting] = useState(false);
  const getCsvExport = useCallback(async () => {
    setExporting(true);

    await exportFn({
      filters: apiParams?.filters,
      sort: apiParams?.sort,
    })
      .then((response: any) => {
        const url = window.URL.createObjectURL(new Blob([response]));
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", exportFileName);
        link.click();
        link.remove();
      })
      .catch(() => {
        toast.error(`An error occurred while exporting ${config.title}`);
      });

    setExporting(false);
  }, [apiParams, config.title, exportFileName, exportFn]);

  return (
    <>
      <UncontrolledDropdown>
        <DropdownToggle
          tag="button"
          className="btn btn-light"
          id="dropdownMenuButton"
        >
          <i className="ri ri-more-fill"></i>
        </DropdownToggle>
        <DropdownMenu>
          <Permission resource={resource} action="import">
            <DropdownItem
              onClick={() => {
                setIsImportModalOpen(true);
              }}
            >
              <i className="ri ri-upload-2-line me-1"></i>
              Import
            </DropdownItem>
          </Permission>
          <Permission resource={resource} action="exportToCsv">
            <DropdownItem
              onClick={() => {
                getCsvExport();
              }}
            >
              <i className="ri ri-download-2-line me-1"></i>
              Export
            </DropdownItem>
          </Permission>
        </DropdownMenu>
      </UncontrolledDropdown>

      <DataImportModal
        toggle={() => setIsImportModalOpen(!isImportModalOpen)}
        isOpen={isImportModalOpen}
        resource={resource}
        config={config}
        onImported={onImported}
      />
      <ExportLoadingModal show={exporting} onCloseClick={() => {}} />
    </>
  );
};

export default ImportExportMenu;
